<template>
  <v-dialog
    v-model="dialog"
    max-width="500">
    <v-card>
      <v-card-title class="text-h5"> Akkumulátor adatai
        <v-spacer />
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-form ref="form" lazy-validation>
          <v-text-field
            label="Megjegyzés"
            :error-messages="errors.description"
            v-model="form.description"
            filled
          />
          <v-menu
            ref="installationMenu"
            v-model="installationMenuOpen"
            :close-on-content-click="false"
            :nudge-width="220"
            min-width="220"
            offset-y
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-bind="attrs"
                v-on="on"
                label="Telepítési dátum"
                :value="form.installation_date"
                :error-messages="errors.installation_date"
                readonly
                filled
              />
            </template>
            <v-date-picker
              v-model="form.installation_date"
              @input="installationMenuOpen = false"
              :max="todayDate"
              no-title
              scrollable
            />
          </v-menu>

          <v-select
            v-if="!showCustomReplacementYears"
            label="Csere ennyi év múlva"
            :items="replacementYearsOptions"
            :error-messages="errors.replacement_due_date"
            v-model="form.replacement_due_date"
            suffix="év"
            filled
            @change="onReplacementYearsChange"
          />

          <v-text-field
            v-if="showCustomReplacementYears"
            label="Csere ennyi év múlva (egyéb)"
            :error-messages="errors.replacement_due_date"
            v-model="customReplacementYears"
            suffix="év"
            filled
            type="number"
            step="1"
            min="1"
            max="20"
            @blur="setCustomReplacementYears"
          >
            <template v-slot:append>
              <v-btn
                icon
                small
                @click="cancelCustomReplacementYears"
                title="Vissza a listához"
              >
                <v-icon small>mdi-close</v-icon>
              </v-btn>
            </template>
          </v-text-field>

          <v-select
            v-if="!showCustomVoltage"
            label="Feszültség"
            :items="voltageOptions"
            :error-messages="errors.voltage"
            v-model="form.voltage"
            suffix="V"
            filled
            @change="onVoltageChange"
          />

          <v-text-field
            v-if="showCustomVoltage"
            label="Feszültség (egyéb)"
            :error-messages="errors.voltage"
            v-model="customVoltage"
            suffix="V"
            filled
            type="number"
            step="0.1"
            min="0.1"
            max="1000"
            @blur="setCustomVoltage"
          >
            <template v-slot:append>
              <v-btn
                icon
                small
                @click="cancelCustomVoltage"
                title="Vissza a listához"
              >
                <v-icon small>mdi-close</v-icon>
              </v-btn>
            </template>
          </v-text-field>

        </v-form>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-spacer />
        <v-btn
          color="primary"
          depressed
          @click="save"
          :loading="saving"
        >
          Mentés
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  props: {
    value: {
      type: Boolean,
      default: false
    },
    controllerId: {
      type: Number,
    }
  },

  data() {
    return {
      form: {
        description: null,
        active: true,
        installation_date: null,
        replacement_due_date: 3,
        voltage: '12',
      },
      errors: {},
      saving: false,
      installationMenuOpen: false,
      showCustomVoltage: false,
      customVoltage: null,
      showCustomReplacementYears: false,
      customReplacementYears: null,
      voltageOptions: [
        { text: '1.5', value: '1.5' },
        { text: '3', value: '3' },
        { text: '6', value: '6' },
        { text: '9', value: '9' },
        { text: '12', value: '12' },
        { text: '24', value: '24' },
        { text: 'Egyéb', value: 'custom' }
      ],
      replacementYearsOptions: [
        { text: '1', value: 1 },
        { text: '2', value: 2 },
        { text: '3', value: 3 },
        { text: '4', value: 4 },
        { text: '5', value: 5 },
        { text: '7', value: 7 },
        { text: '10', value: 10 },
        { text: 'Egyéb', value: 'custom' }
      ]
    };
  },

  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      }
    },

    todayDate() {
      const today = new Date();
      const year = today.getFullYear();
      const month = (`0${today.getMonth() + 1}`).slice(-2);
      const day = (`0${today.getDate()}`).slice(-2);
      return `${year}-${month}-${day}`;
    }
  },

  watch: {
    value(newVal) {
      if (newVal) {
        this.resetForm();
      }
    }
  },

  methods: {
    async save() {
      this.saving = true;
      this.errors = {};

      try {
        const payload = {
          ...this.form,
          controller_id: this.controllerId
        };

        const response = await this.$http.post('controller/saveAccumulator', payload);

        if (response.errors && Object.keys(response.errors).length === 0) {
          this.$dialog.notify.success('Akkumulátor sikeresen létrehozva');
          this.$emit('save');
          this.close();
        } else {
          if (response.errors && Object.keys(response.errors).length > 0) {
            this.errors = response.errors;
            this.$dialog.notify.error(Object.values(response.errors)[0]);
          }
        }
      } catch (error) {
        this.$dialog.notify.error(error.message || error);
      } finally {
        this.saving = false;
      }
    },

    close() {
      this.dialog = false;
      this.resetForm();
    },

    resetForm() {
      this.form = {
        description: null,
        active: true,
        installation_date: null,
        replacement_due_date: null,
        voltage: null,
      };
      this.errors = {};
      this.showCustomVoltage = false;
      this.customVoltage = null;
      this.showCustomReplacementYears = false;
      this.customReplacementYears = null;
    },

    onVoltageChange(value) {
      if (value === 'custom') {
        this.showCustomVoltage = true;
        this.form.voltage = null;
        this.$nextTick(() => {
          const customField = this.$el.querySelector('input[type="number"][step="0.1"]');
          if (customField) {
            customField.focus();
          }
        });
      } else {
        this.form.voltage = value;
        this.showCustomVoltage = false;
        this.customVoltage = null;
      }
    },

    setCustomVoltage() {
      if (this.customVoltage && !isNaN(this.customVoltage) && parseFloat(this.customVoltage) > 0) {
        this.form.voltage = parseFloat(this.customVoltage).toString();
      }
    },

    cancelCustomVoltage() {
      this.showCustomVoltage = false;
      this.customVoltage = null;
      this.form.voltage = null;
    },

    onReplacementYearsChange(value) {
      if (value === 'custom') {
        this.showCustomReplacementYears = true;
        this.form.replacement_due_date = null;
        this.$nextTick(() => {
          const customField = this.$el.querySelector('input[type="number"][step="1"]');
          if (customField) {
            customField.focus();
          }
        });
      } else {
        this.form.replacement_due_date = value;
        this.showCustomReplacementYears = false;
        this.customReplacementYears = null;
      }
    },

    setCustomReplacementYears() {
      if (this.customReplacementYears && !isNaN(this.customReplacementYears) && parseInt(this.customReplacementYears) > 0) {
        this.form.replacement_due_date = parseInt(this.customReplacementYears);
      }
    },

    cancelCustomReplacementYears() {
      this.showCustomReplacementYears = false;
      this.customReplacementYears = null;
      this.form.replacement_due_date = null;
    }
  },
};
</script>